import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { ComponentsModule, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthenticationService } from '@lib/auth/authentication.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { DataUploadService, PackageData, PackageUploadResponse, UploadedPackage } from '../../services/data-upload.service';

type CsvData = Record<string, string | number | null>;

interface CsvColumnConfig {
    name: string;
    required: boolean;
    defaultValue?: string | number | null;
    comment: string;
    validation: string;
    example: string;
}

// Column configuration based on requirements and backend validation
const CSV_COLUMNS: CsvColumnConfig[] = [
    {
        name: 'reference',
        required: true,
        comment: 'Unique package identifier for tracking and referencing',
        validation: 'String: 3-50 characters, must be unique',
        example: 'PKG001, REF-2025-001'
    },
    {
        name: 'created_at',
        required: true,
        comment: 'Package creation timestamp in ISO format',
        validation: 'Date: ISO 8601 format (YYYY-MM-DD HH:MM:SS)',
        example: '2025-01-15 09:30:00'
    },
    {
        name: 'carers_required',
        required: false,
        defaultValue: 0,
        comment: 'Number of carers needed for this package',
        validation: 'Integer: 0-10',
        example: '1, 2, 3'
    },
    {
        name: 'open',
        required: false,
        defaultValue: 0,
        comment: 'Package availability status (0=closed, 1=open)',
        validation: 'Boolean: 0 or 1',
        example: '0, 1'
    },
    {
        name: 'support_required',
        required: false,
        comment: 'Detailed description of care support needed',
        validation: 'String: Up to 1000 characters',
        example: 'Live-in care, medication management'
    },
    {
        name: 'details',
        required: false,
        comment: 'Additional package information and specifications',
        validation: 'String: Up to 1000 characters',
        example: '24/7 care for elderly client with mobility issues'
    },
    {
        name: 'start_date',
        required: true,
        comment: 'Package service commencement date',
        validation: 'Date: YYYY-MM-DD format, must be valid date',
        example: '2025-01-15, 2025-02-01'
    },
    {
        name: 'end_date',
        required: true,
        comment: 'Package service completion date',
        validation: 'Date: YYYY-MM-DD format, must be after start_date',
        example: '2025-03-15, 2025-04-01'
    },
    {
        name: 'weekly_hours',
        required: false,
        comment: 'Total hours of care required per week',
        validation: 'Decimal: 0-168 hours, up to 2 decimal places',
        example: '40.5, 168, 25.75'
    },
    {
        name: 'commissioner',
        required: true,
        comment: 'Commissioning organization or authority name',
        validation: 'String: Must exist in commissioners database',
        example: 'Hampshire County Council, NHS Trust'
    },
    {
        name: 'clients',
        required: false,
        comment: 'Client reference codes associated with this package',
        validation: 'String: Comma-separated list of client reference codes',
        example: 'CLT001, CLT-2025-002'
    },
    {
        name: 'street_1',
        required: true,
        comment: 'Primary street address line',
        validation: 'String: Up to 255 characters',
        example: '123 Main Street, 45 Oak Avenue'
    },
    {
        name: 'street_2',
        required: false,
        comment: 'Secondary address line (apartment, suite, etc.)',
        validation: 'String: Up to 255 characters',
        example: 'Apt 2B, Suite 100, Floor 3'
    },
    {
        name: 'town',
        required: true,
        comment: 'Town or city name for the address',
        validation: 'String: Up to 60 characters',
        example: 'Southampton, Portsmouth, London'
    },
    {
        name: 'post_code',
        required: false,
        comment: 'Postal code for the address location',
        validation: 'String: 5-10 characters when provided',
        example: 'SO14 2AA, PO1 2EF, SW1A 1AA'
    },
    {
        name: 'county',
        required: false,
        comment: 'County or administrative region',
        validation: 'String: 4-20 characters when provided',
        example: 'Hampshire, Surrey, Kent'
    },
    {
        name: 'country',
        required: false,
        comment: 'Country name for international addresses',
        validation: 'String: 4-56 characters when provided',
        example: 'United Kingdom, Ireland'
    },
    {
        name: 'lat',
        required: false,
        comment: 'Latitude coordinate for precise location',
        validation: 'Decimal: Valid latitude (-90 to 90)',
        example: '50.9097, 51.5074'
    },
    {
        name: 'lng',
        required: false,
        comment: 'Longitude coordinate for precise location',
        validation: 'Decimal: Valid longitude (-180 to 180)',
        example: '-1.4044, -0.1278'
    },
    {
        name: 'provider',
        required: false,
        defaultValue: null,
        comment: 'Assigned care provider organization',
        validation: 'String: Must exist in providers database or null',
        example: 'CareProvider Ltd, ABC Care Services'
    },
    {
        name: 'care_option',
        required: true,
        comment: 'Type of care service required for this package',
        validation: 'String: Must exist in care options database',
        example: 'Live-in Care, Respite Care, Dementia Care'
    },
    {
        name: 'region',
        required: true,
        comment: 'Geographic region for the package',
        validation: 'String: Must exist in regions database',
        example: 'South East, North West, London'
    },
    {
        name: 'zone',
        required: true,
        comment: 'Specific zone within the region',
        validation: 'String: Must exist in zones database',
        example: 'Zone A, Zone B, Central Zone'
    }
];

@Component({
    selector: 'app-settings-data-upload-index-page',
    imports: [
        HeaderComponent,
        ComponentsModule,
        ReactiveFormsModule,
        CommonModule
    ],
    templateUrl: './settings-data-upload-index-page.component.html',
    standalone: true,
    styleUrl: './settings-data-upload-index-page.component.scss'
})
export class SettingsDataUploadIndexPageComponent {
    private readonly _authService = inject(AuthenticationService);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _dataUploadService = inject(DataUploadService);
    private readonly _router = inject(Router);

    protected user = toSignal(this._authService.user$);
    protected csvData = signal<CsvData[]>([]);
    protected csvHeaders = signal<string[]>([]);
    protected fileName = signal<string>('');
    protected isLoading = signal<boolean>(false);
    protected validationErrors = signal<string[]>([]);
    protected isValidCsv = signal<boolean>(false);
    protected isUploading = signal<boolean>(false);
    protected columnConfigs = signal<CsvColumnConfig[]>(CSV_COLUMNS);
    protected uploadedPackages = signal<UploadedPackage[]>([]);

    /** Computed property to check if file is loaded */
    protected isFileLoaded = computed(() =>
        this.fileName() !== '' || this.csvData().length > 0 || this.isValidCsv()
    );

    /** The file input form control */
    protected readonly fileControl = new FormControl<File | null>(null, [
        Validators.required
    ]);

    protected onFileSelected(event: Event): void {
        // Prevent file selection if a file is already loaded
        if (this.isFileLoaded()) {
            this._snackBar.open({
                colour: 'warning',
                dismissable: false,
                icon: 'warning',
                title: 'Please clear the current data before uploading a new file.',
                actions: [{ id: 'ok', label: 'OK' }]
            });
            return;
        }

        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];

            // Validate file type
            if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
                this._snackBar.open({
                    colour: 'error',
                    dismissable: false,
                    icon: 'error_outline',
                    title: 'Please select a CSV file.',
                    actions: [{ id: 'ok', label: 'OK' }]
                });
                return;
            }

            this.fileControl.setValue(file);
            this.fileName.set(file.name);
            this.parseCsvFile(file);
        }
    }

    private parseCsvFile(file: File): void {
        this.isLoading.set(true);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const csv = e.target?.result as string;
                const lines = csv.split('\n').filter(line => line.trim());

                if (lines.length === 0) {
                    this.csvHeaders.set([]);
                    this.csvData.set([]);
                    this.isLoading.set(false);
                    this._snackBar.open({
                        colour: 'error',
                        dismissable: false,
                        icon: 'warning',
                        title: 'CSV file is empty.',
                        actions: [{ id: 'ok', label: 'OK' }]
                    });
                    return;
                }

                // Parse headers from first line
                const headers = this.parseCSVLine(lines[0]);

                // Validate headers against required columns
                const validationResult = this.validateHeaders(headers);

                if (!validationResult.isValid) {
                    this.validationErrors.set(validationResult.errors);
                    this.isValidCsv.set(false);
                    this.isLoading.set(false);

                    this._snackBar.open({
                        colour: 'error',
                        dismissable: false,
                        icon: 'error_outline',
                        title: 'CSV validation failed. Check the column requirements.',
                        actions: [{ id: 'ok', label: 'OK' }]
                    });
                    return;
                }

                this.csvHeaders.set(headers);
                this.validationErrors.set([]);
                this.isValidCsv.set(true);

                // Parse and process data from remaining lines
                const data: CsvData[] = [];
                for (let i = 1; i < lines.length; i++) {
                    let values = this.parseCSVLine(lines[i]);

                    while (values.length < headers.length) {
                        values.push('');
                    }

                    if (values.length > headers.length) {
                        values = values.slice(0, headers.length);
                    }

                    const row = this.processDataRow(headers, values);
                    data.push(row);
                }

                this.csvData.set(data);
                this.isLoading.set(false);

                this._snackBar.open({
                    colour: 'success',
                    dismissable: false,
                    icon: 'check_circle',
                    title: `Successfully loaded ${data.length} rows. Required columns validated and defaults applied.`,
                    actions: [{ id: 'ok', label: 'OK' }]
                });

            } catch {
                this.isLoading.set(false);
                this._snackBar.open({
                    colour: 'error',
                    dismissable: false,
                    icon: 'error_outline',
                    title: 'Error parsing CSV file. Please check the file format.',
                    actions: [{ id: 'ok', label: 'OK' }]
                });
            }
        };

        reader.onerror = () => {
            this.isLoading.set(false);
            this._snackBar.open({
                colour: 'error',
                dismissable: false,
                icon: 'error_outline',
                title: 'Error reading file.',
                actions: [{ id: 'ok', label: 'OK' }]
            });
        };

        reader.readAsText(file);
    }

    private validateHeaders(headers: string[]): { isValid: boolean; errors: string[] } {
        const errors: string[] = [];

        // Check for required columns
        const requiredColumns = CSV_COLUMNS.filter(col => col.required).map(col => col.name);
        const missingRequired = requiredColumns.filter(col => !headers.includes(col));

        if (missingRequired.length > 0) {
            errors.push(`Missing required columns: ${missingRequired.join(', ')}`);
        }

        // Check for unknown columns
        const validColumnNames = CSV_COLUMNS.map(col => col.name);
        const unknownColumns = headers.filter(header => !validColumnNames.includes(header));

        if (unknownColumns.length > 0) {
            errors.push(`Unknown columns found: ${unknownColumns.join(', ')}`);
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    private processDataRow(headers: string[], values: string[]): CsvData {
        const row: CsvData = {};

        // Process each column according to configuration
        CSV_COLUMNS.forEach(config => {
            const headerIndex = headers.indexOf(config.name);

            if (headerIndex >= 0) {
                // Column exists in CSV
                const value = values[headerIndex]?.trim();

                if (value === '' || value === undefined) {
                    // Empty value
                    if (config.required) {
                        row[config.name] = `[REQUIRED - MISSING VALUE]`;
                    } else if (config.defaultValue !== undefined) {
                        row[config.name] = config.defaultValue;
                    } else {
                        row[config.name] = '';
                    }
                } else {
                    row[config.name] = value;
                }
            } else if (config.required) {
                // Column doesn't exist in CSV and is required
                row[config.name] = `[REQUIRED - COLUMN MISSING]`;
            } else if (config.defaultValue !== undefined) {
                // Column doesn't exist but has default value
                row[config.name] = config.defaultValue;
            } else {
                // Column doesn't exist and no default
                row[config.name] = '';
            }
        });

        return row;
    }

    private parseCSVLine(line: string): string[] {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;

        for (const char of line) {
            if (char === '"') {
                inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }

        // Push the last field
        result.push(current.trim());

        // Clean up quotes from each value
        return result.map(val => val.replace(/^"|"$/g, '').trim());
    }

    protected clearData(): void {
        this.csvData.set([]);
        this.csvHeaders.set([]);
        this.fileName.set('');
        this.validationErrors.set([]);
        this.isValidCsv.set(false);
        this.isUploading.set(false);
        this.uploadedPackages.set([]);
        this.fileControl.setValue(null);

        // Clear the file input
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }

        this._snackBar.open({
            colour: 'success',
            dismissable: false,
            icon: 'info',
            title: 'Data cleared successfully.',
            actions: [{ id: 'ok', label: 'OK' }]
        });
    }


    protected isRequiredError(value: string | number | null): boolean {
        return String(value || '').includes('[REQUIRED');
    }

    protected downloadTemplate(): void {
        // Create CSV template content with just headers
        const headers = CSV_COLUMNS.map(col => col.name);
        const csvContent = headers.join(',') + '\n';

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'csv-template.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this._snackBar.open({
            colour: 'success',
            dismissable: false,
            icon: 'download',
            title: 'CSV template downloaded successfully.',
            actions: [{ id: 'ok', label: 'OK' }]
        });
    }

    protected onDisabledUploadClick(): void {
        this._snackBar.open({
            colour: 'warning',
            dismissable: false,
            icon: 'warning',
            title: 'Upload is disabled. Clear the current data first.',
            actions: [{ id: 'ok', label: 'OK' }]
        });
    }

    protected viewPackage(packageId: number): void {
        this._router.navigate(['/packages', packageId]);
    }

    protected uploadPackageData(): void {
        if (this.csvData().length === 0 || !this.isValidCsv()) {
            this._snackBar.open({
                colour: 'error',
                dismissable: false,
                icon: 'error_outline',
                title: 'No valid data to upload.',
                actions: [{ id: 'ok', label: 'OK' }]
            });
            return;
        }

        const hasRequiredPlaceholders = this.csvData().some(row =>
            Object.values(row).some(val => String(val ?? '').startsWith('[REQUIRED'))
        );

        if (hasRequiredPlaceholders) {
            this._snackBar.open({
                colour: 'error',
                dismissable: false,
                icon: 'error_outline',
                title: 'Some required fields are missing. Fix the highlighted cells before uploading.',
                actions: [{ id: 'ok', label: 'OK' }]
            });
            return;
        }

        this.isUploading.set(true);

        // Convert CSV data to PackageData format
        const packageData: PackageData[] = this.csvData().map(row => ({
            reference: String(row['reference'] || ''),
            created_at: String(row['created_at'] || ''),
            carers_required: Number(row['carers_required']) || 0,
            open: Boolean(row['open']) || false,
            support_required: String(row['support_required'] || ''),
            details: String(row['details'] || ''),
            start_date: String(row['start_date'] || ''),
            end_date: String(row['end_date'] || ''),
            weekly_hours: row['weekly_hours'] != null && row['weekly_hours'] !== '' && !isNaN(Number(row['weekly_hours'])) ? Number(row['weekly_hours']) : undefined,
            commissioner: String(row['commissioner'] || ''),
            clients: String(row['clients'] || ''),
            street_1: String(row['street_1'] || ''),
            street_2: String(row['street_2'] || ''),
            town: String(row['town'] || ''),
            post_code: String(row['post_code'] || ''),
            county: String(row['county'] || ''),
            country: String(row['country'] || ''),
            lat: Number(row['lat']) || undefined,
            lng: Number(row['lng']) || undefined,
            provider: row['provider'] ? String(row['provider']) : undefined,
            care_option: String(row['care_option'] || ''),
            region: String(row['region'] || ''),
            zone: String(row['zone'] || '')
        }));

        // Call the API service
        this._dataUploadService.uploadPackages(packageData).subscribe({
            next: (response: PackageUploadResponse) => {
                this.isUploading.set(false);

                if (response.success) {
                    const hasErrors = response.data?.errors && response.data.errors.length > 0;

                    // Store uploaded packages
                    if (response.data?.uploaded_packages) {
                        this.uploadedPackages.set(response.data.uploaded_packages);
                    }

                    if (hasErrors && response.data?.errors) {
                        this.validationErrors.set(response.data.errors);
                        this._snackBar.open({
                            colour: 'warning',
                            dismissable: false,
                            icon: 'warning',
                            title: `${response.message} (${response.data?.failed_count || 0} packages failed validation)`,
                            actions: [{ id: 'ok', label: 'OK' }]
                        });
                    } else {
                        this._snackBar.open({
                            colour: 'success',
                            dismissable: false,
                            icon: 'check_circle',
                            title: response.message,
                            actions: [{ id: 'ok', label: 'OK' }]
                        });

                        // Clear CSV data but keep uploaded packages to show the table
                        this.csvData.set([]);
                        this.csvHeaders.set([]);
                        this.fileName.set('');
                        this.validationErrors.set([]);
                        this.isValidCsv.set(false);
                        this.fileControl.setValue(null);

                        // Clear the file input
                        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
                        if (fileInput) {
                            fileInput.value = '';
                        }
                    }
                } else {
                    if (response.data?.errors && response.data.errors.length > 0) {
                        this.validationErrors.set(response.data.errors);
                    }

                    const errorCount = response.data?.errors?.length || 0;
                    const titleWithCount = errorCount > 0
                        ? `${response.message || 'Upload failed.'} (${errorCount} validation errors)`
                        : response.message || 'Upload failed.';

                    this._snackBar.open({
                        colour: 'error',
                        dismissable: false,
                        icon: 'error_outline',
                        title: titleWithCount,
                        actions: [{ id: 'ok', label: 'OK' }]
                    });
                }
            },
            error: (error: unknown) => {
                this.isUploading.set(false);

                let errorMessage = 'Upload failed. Please check your data and try again.';
                let errorList: string[] = [];

                if (error && typeof error === 'object' && 'error' in error) {
                    const errorObj = error as { error?: { data?: { errors?: string[] }; message?: string }; message?: string };

                    if (errorObj.error?.data?.errors) {
                        errorList = errorObj.error.data.errors;
                        errorMessage = errorObj.error.message || errorMessage;
                    } else if (errorObj.error?.message) {
                        errorMessage = errorObj.error.message;
                    } else if (errorObj.message) {
                        errorMessage = errorObj.message;
                    }
                }

                this.validationErrors.set(errorList);

                const titleWithCount = errorList.length > 0
                    ? `${errorMessage} (${errorList.length} validation errors found)`
                    : errorMessage;

                this._snackBar.open({
                    colour: 'error',
                    dismissable: false,
                    icon: 'error_outline',
                    title: titleWithCount,
                    actions: [{ id: 'ok', label: 'OK' }]
                });
            }
        });
    }
}
